import dayjs from "dayjs";

import { fhirClients } from "src-next/shared/api/fhirClient";
import { normalizeHttpError } from "src-next/shared/api/httpClient";

import {
  QUESTIONNAIRE_TASK_CODE,
  QUESTIONNAIRE_TASK_CODE_SYSTEM,
  QUESTIONNAIRE_TASK_CODE_TEXT,
  QUESTIONNAIRE_TASK_SCHEDULING_APPOINTMENT_IDENTIFIER_SYSTEM,
} from "../constants/questionnaireTaskFhirConstants";

export interface CreateQuestionnaireTaskParams {
  /** ID lógico da organização (mesmo uso que QuestionnaireManagementService). */
  organizationId: string;
  /** Ex.: PractitionerRole/{orgId}-{oids.cpf}-{cpf} */
  practitionerRoleReference: string;
  /** Ex.: Patient/{id} */
  patientReference: string;
  /** ID lógico do Questionnaire no FHIR admin. */
  questionnaireId: string;
  questionnaireTitle: string;
  /**
   * Quando o disparo vem de um agendamento conhecido, entrada no formato usual
   * `Appointment/{id}` ou id lógico; persiste como `Task.identifier` no workflow
   * (valor puro em `identifier.value`; ver constante de system em `questionnaireTaskFhirConstants`).
   */
  appointmentReference?: string;
}

const APPOINTMENT_ID_PLACEHOLDERS = new Set(
  ["", "appointment", "ad-hoc", "ad-hoc-messaging"].map((s) => s.toLowerCase()),
);

/**
 * Normaliza entrada de agendamento (referência ou id): retorna `Appointment/{id}` só quando
 * o id for plausivelmente o recurso real. Placeholders de UI (ex. `ad-hoc`) → `undefined`.
 * Usado pelos callers com `appointmentReference`, na leitura de `identifier.value` e no
 * fallback de `basedOn` legado.
 */
export function resolveOptionalAppointmentBasedOn(
  idOrReference: string | number | null | undefined,
): string | undefined {
  if (idOrReference === null || idOrReference === undefined) return undefined;
  const raw = String(idOrReference).trim();
  if (!raw) return undefined;
  const idPart = raw.startsWith("Appointment/")
    ? raw.slice("Appointment/".length).trim()
    : raw;
  if (!idPart) return undefined;
  if (APPOINTMENT_ID_PLACEHOLDERS.has(idPart.toLowerCase())) return undefined;
  if (idPart.toLowerCase().startsWith("dashboard-")) return undefined;
  return raw.startsWith("Appointment/") ? raw : `Appointment/${idPart}`;
}

/**
 * Valor a gravar em `Task.identifier[].value`: mesma semântica de placeholders que
 * `resolveOptionalAppointmentBasedOn`, sem prefixo `Appointment/`.
 */
export function resolveOptionalAppointmentIdentifierValue(
  idOrReference: string | number | null | undefined,
): string | undefined {
  const ref = resolveOptionalAppointmentBasedOn(idOrReference);
  if (!ref) return undefined;
  return ref.slice("Appointment/".length);
}

/**
 * Lê da Task de questionário a referência normalizada `Appointment/{id}` (identifier
 * estável; fallback em `basedOn` legado).
 */
export function getAppointmentReferenceFromQuestionnaireTask(
  task: fhir5.Task,
): string | undefined {
  const identifiers = task.identifier;
  if (identifiers?.length) {
    for (const ident of identifiers) {
      if (
        ident?.system === QUESTIONNAIRE_TASK_SCHEDULING_APPOINTMENT_IDENTIFIER_SYSTEM
      ) {
        const v = typeof ident.value === "string" ? ident.value.trim() : "";
        if (v) {
          const fromValue = resolveOptionalAppointmentBasedOn(v);
          if (fromValue) return fromValue;
        }
      }
    }
  }

  const basedOn = task.basedOn;
  if (!basedOn) return undefined;
  const refs = Array.isArray(basedOn) ? basedOn : [basedOn];
  for (const ref of refs) {
    const r = ref?.reference?.trim();
    if (typeof r === "string" && r.startsWith("Appointment/")) {
      return resolveOptionalAppointmentBasedOn(r);
    }
  }
  return undefined;
}

function assertNonEmpty(label: string, value: string | undefined | null) {
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    throw new Error(
      `${label} é obrigatório para criar a task de questionário.`,
    );
  }
}

/**
 * Monta o recurso Task (FHIR R5) para questionário pendente.
 *
 * Documentação do contrato: ver `questionnaireTaskFhirConstants.ts`.
 */
export function buildQuestionnaireTaskResource(
  params: CreateQuestionnaireTaskParams,
): fhir5.Task {
  assertNonEmpty("organizationId", params.organizationId);
  assertNonEmpty("practitionerRoleReference", params.practitionerRoleReference);
  assertNonEmpty("patientReference", params.patientReference);
  assertNonEmpty("questionnaireId", params.questionnaireId);

  const authoredOn = dayjs().toISOString();

  const appointmentIdentifierValue = resolveOptionalAppointmentIdentifierValue(
    params.appointmentReference,
  );

  return {
    resourceType: "Task",
    status: "requested",
    intent: "order",
    priority: "routine",
    authoredOn,
    description: `${params.questionnaireTitle.trim() || "Questionário"}`,
    owner: { reference: `Organization/${params.organizationId.trim()}` },
    requester: { reference: params.practitionerRoleReference.trim() },
    for: { reference: params.patientReference.trim() },
    focus: { reference: `Questionnaire/${params.questionnaireId.trim()}` },
    ...(appointmentIdentifierValue
      ? {
          identifier: [
            {
              system:
                QUESTIONNAIRE_TASK_SCHEDULING_APPOINTMENT_IDENTIFIER_SYSTEM,
              value: appointmentIdentifierValue,
            },
          ],
        }
      : {}),
    code: {
      text: QUESTIONNAIRE_TASK_CODE_TEXT,
      coding: [
        {
          system: QUESTIONNAIRE_TASK_CODE_SYSTEM,
          code: QUESTIONNAIRE_TASK_CODE,
        },
      ],
    },
  };
}

function getTaskClient() {
  return fhirClients.workflow.resource<fhir5.Task>("Task");
}

/**
 * Cria uma Task de questionário no servidor workflow.
 */
export async function createQuestionnaireTask(
  params: CreateQuestionnaireTaskParams,
): Promise<fhir5.Task> {
  const resource = buildQuestionnaireTaskResource(params);
  try {
    return await getTaskClient().create(resource);
  } catch (error) {
    const httpError = normalizeHttpError(error);
    throw new Error(
      httpError.message || "Falha ao criar task de questionário no workflow.",
    );
  }
}

/**
 * Cria várias tasks (uma por questionário). Falha na primeira erro retornado pelo servidor.
 */
export async function createQuestionnaireTasks(
  paramsList: CreateQuestionnaireTaskParams[],
): Promise<fhir5.Task[]> {
  const results: fhir5.Task[] = [];
  for (const params of paramsList) {
    // eslint-disable-next-line no-await-in-loop
    const created = await createQuestionnaireTask(params);
    results.push(created);
  }
  return results;
}

/**
 * Paciente (ou cliente autorizado) marca a task como respondida — status `completed`.
 * Mantém paridade com `PatchTask` do legado (JSON Patch no workflow).
 */
export async function patchQuestionnaireTaskMarkCompleted(
  taskId: string,
): Promise<fhir5.Task> {
  const trimmedId = taskId?.trim();
  if (!trimmedId) {
    throw new Error("ID da task é obrigatório.");
  }
  try {
    return await getTaskClient().patch(trimmedId, [
      { op: "replace", path: "/status", value: "completed" },
    ]);
  } catch (error) {
    const httpError = normalizeHttpError(error);
    throw new Error(
      httpError.message || "Falha ao atualizar task de questionário.",
    );
  }
}

export const questionnaireTaskWorkflowService = {
  buildQuestionnaireTaskResource,
  createQuestionnaireTask,
  createQuestionnaireTasks,
  patchQuestionnaireTaskMarkCompleted,
  resolveOptionalAppointmentBasedOn,
  resolveOptionalAppointmentIdentifierValue,
  getAppointmentReferenceFromQuestionnaireTask,
};
